import { useState, useCallback, useMemo } from 'react';

export type PawPrint = {
  image: string | null;
  alt: string | null;
  firstName: string | null;
  lastName: string | null;
  content: string | null;
  createdAt: string | null;
};

type UsePawPrintCarouselArgs = {
  pawPrints: PawPrint[];
};

type UsePawPrintCarouselReturn = {
  currentIndex: number;
  currentPawPrint: PawPrint | undefined;
  totalPawPrints: number;
  goToPrevious: () => void;
  goToNext: () => void;
  selectPawPrint: (index: number) => void;
  handleKeyDown: (event: React.KeyboardEvent) => void;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '';
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short'
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

export const formatPawPrintDate = formatDate;

export const formatPawPrintName = (
  firstName: string | null,
  lastName: string | null
) => {
  const first = firstName || '';
  const lastInitial = lastName?.charAt(0) || '';
  return `${first} ${lastInitial}.`.trim();
};

export function usePawPrintCarousel({
  pawPrints
}: UsePawPrintCarouselArgs): UsePawPrintCarouselReturn {
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalPawPrints = pawPrints.length;

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? totalPawPrints - 1 : prevIndex - 1
    );
  }, [totalPawPrints]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === totalPawPrints - 1 ? 0 : prevIndex + 1
    );
  }, [totalPawPrints]);

  const selectPawPrint = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        goToNext();
      }
    },
    [goToPrevious, goToNext]
  );

  const currentPawPrint = useMemo(
    () => pawPrints[currentIndex],
    [pawPrints, currentIndex]
  );

  return {
    currentIndex,
    currentPawPrint,
    totalPawPrints,
    goToPrevious,
    goToNext,
    selectPawPrint,
    handleKeyDown
  };
}
